import { combineReducers, configureStore } from "@reduxjs/toolkit"
import { setupListeners } from "@reduxjs/toolkit/query"
import { toast } from "react-toastify"
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import { authApiService } from "../Apis/Auth"
import { feedbackApiService } from "../Apis/Feedback"
import { userApiService } from "../Apis/User"
import { adminApiService } from "../Apis/Admin"
import { neighborhoodApiService } from "../Apis/Neighborhood"

import { adsApiService } from "../Apis/Ads"
import { bannerApiService } from "../Apis/Banner"
import { bookingApiService } from "../Apis/Booking"
import { brandsApiService } from "../Apis/Brands"
import { categoryApiService } from "../Apis/Category"
import { discountApiService } from "../Apis/Discount"
import { generalApiService } from "../Apis/General"
import { guidelinesApiService } from "../Apis/Guidelines"
import { issueApiService } from "../Apis/Issue"
import { notificationApiService } from "../Apis/Notification"
import { orderApiService } from "../Apis/Order"
import { packagesApiService } from "../Apis/Package"
import { productApiService } from "../Apis/Product"
import { promosApiService } from "../Apis/Promo"
import { pushnotificationApiService } from "../Apis/PushNotification"
import { refundsApiService } from "../Apis/Refund"
import { reviewApiService } from "../Apis/Review"
import { servicesApiService } from "../Apis/Services"
import { sitterApiService } from "../Apis/Sitter"
import { subcategoryApiService } from "../Apis/Subcategory"
import { withdrawlsApiService } from "../Apis/Withdrawl"
import { vendorReviewApiService } from "../Apis/VendorReview"
import { vendorAdsApiService } from "../Apis/VendorAds"
import { adPricingApiService } from "../Apis/AdPricing"
import { familyApiService } from "../Apis/Family"
import { navigationApiService } from "../Apis/Navigation"
import { forumApiService } from "../Apis/Forum"
import { reportApiService } from "../Apis/Report"
import AuthSlice, { logout } from "../Slices/Auth"
import GeneralSlice from "../Slices/General"

let lastUnauthorizedToastAt = 0

const apiErrorHandler = (store) => (next) => (action) => {
  if (action.type.endsWith('/rejected')) {
    // Handle case when payload is undefined (e.g., network error, server crashed)
    if (!action.payload) {
      toast.error('Network error. Please check if the server is running.')
      return next(action)
    }

    let { data, status } = action.payload
    let message = data?.message || 'An error occurred'

    if (status === 401) {
      const { isLoggedIn, token } = store.getState().authSlice || {}
      if (isLoggedIn || token) {
        store.dispatch(logout())

        if (Date.now() - lastUnauthorizedToastAt > 3000) {
          toast.error('Session expired. Please sign in again.')
          lastUnauthorizedToastAt = Date.now()
        }
      }

      return next(action)
    }

    toast.error(message)

    if (status === 500) {
      store.dispatch(logout())
    }
  }

  return next(action);
};

export const apiServices = [
  authApiService,
  userApiService,
  adminApiService,
  neighborhoodApiService,
  feedbackApiService,
  notificationApiService,
  sitterApiService,
  brandsApiService,
  categoryApiService,
  productApiService,
  subcategoryApiService,
  generalApiService,
  orderApiService,
  discountApiService,
  bannerApiService,
  issueApiService,
  guidelinesApiService,
  packagesApiService,
  servicesApiService,
  bookingApiService,
  pushnotificationApiService,
  reviewApiService,
  withdrawlsApiService,
  refundsApiService,
  adsApiService,
  promosApiService,
  vendorReviewApiService,
  vendorAdsApiService,
  adPricingApiService,
  familyApiService,
  navigationApiService,
  forumApiService,
  reportApiService,
]

const logoutCleanupHandler = (store) => (next) => (action) => {
  const result = next(action)

  if (action.type === logout.type) {
    apiServices.forEach((service) => {
      store.dispatch(service.util.resetApiState())
    })
  }

  return result
}

const persistConfig = {
  key: 'theturnat-admin-root',
  storage: storage
}

export const rootReducers = combineReducers({
  authSlice: AuthSlice,
  generalSlice: GeneralSlice,
  [authApiService.reducerPath]: authApiService.reducer,
  [userApiService.reducerPath]: userApiService.reducer,
  [adminApiService.reducerPath]: adminApiService.reducer,
  [neighborhoodApiService.reducerPath]: neighborhoodApiService.reducer,
  [feedbackApiService.reducerPath]: feedbackApiService.reducer,
  [notificationApiService.reducerPath]: notificationApiService.reducer,
  [sitterApiService.reducerPath]: sitterApiService.reducer,
  [brandsApiService.reducerPath]: brandsApiService.reducer,
  [categoryApiService.reducerPath]: categoryApiService.reducer,
  [productApiService.reducerPath]: productApiService.reducer,
  [subcategoryApiService.reducerPath]: subcategoryApiService.reducer,
  [generalApiService.reducerPath]: generalApiService.reducer,
  [orderApiService.reducerPath]: orderApiService.reducer,
  [discountApiService.reducerPath]: discountApiService.reducer,
  [bannerApiService.reducerPath]: bannerApiService.reducer,
  [issueApiService.reducerPath]: issueApiService.reducer,
  [guidelinesApiService.reducerPath]: guidelinesApiService.reducer,
  [packagesApiService.reducerPath]: packagesApiService.reducer,
  [servicesApiService.reducerPath]: servicesApiService.reducer,
  [bookingApiService.reducerPath]: bookingApiService.reducer,
  [pushnotificationApiService.reducerPath]: pushnotificationApiService.reducer,
  [reviewApiService.reducerPath]: reviewApiService.reducer,
  [withdrawlsApiService.reducerPath]: withdrawlsApiService.reducer,
  [refundsApiService.reducerPath]: refundsApiService.reducer,
  [adsApiService.reducerPath]: adsApiService.reducer,
  [promosApiService.reducerPath]: promosApiService.reducer,
  [vendorReviewApiService.reducerPath]: vendorReviewApiService.reducer,
  [vendorAdsApiService.reducerPath]: vendorAdsApiService.reducer,
  [adPricingApiService.reducerPath]: adPricingApiService.reducer,
  [familyApiService.reducerPath]: familyApiService.reducer,
  [navigationApiService.reducerPath]: navigationApiService.reducer,
  [forumApiService.reducerPath]: forumApiService.reducer,
  [reportApiService.reducerPath]: reportApiService.reducer,
})

const persistedReducer = persistReducer(persistConfig, rootReducers)

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
    },
  })
    .concat(authApiService.middleware)
    .concat(userApiService.middleware)
    .concat(adminApiService.middleware)
    .concat(neighborhoodApiService.middleware)
    .concat(feedbackApiService.middleware)
    .concat(notificationApiService.middleware)
    .concat(sitterApiService.middleware)
    .concat(brandsApiService.middleware)
    .concat(categoryApiService.middleware)
    .concat(productApiService.middleware)
    .concat(subcategoryApiService.middleware)
    .concat(generalApiService.middleware)
    .concat(orderApiService.middleware)
    .concat(discountApiService.middleware)
    .concat(bannerApiService.middleware)
    .concat(issueApiService.middleware)
    .concat(guidelinesApiService.middleware)
    .concat(packagesApiService.middleware)
    .concat(servicesApiService.middleware)
    .concat(bookingApiService.middleware)
    .concat(pushnotificationApiService.middleware)
    .concat(reviewApiService.middleware)
    .concat(withdrawlsApiService.middleware)
    .concat(refundsApiService.middleware)
    .concat(adsApiService.middleware)
    .concat(promosApiService.middleware)
    .concat(vendorReviewApiService.middleware)
    .concat(vendorAdsApiService.middleware)
    .concat(adPricingApiService.middleware)
    .concat(familyApiService.middleware)
    .concat(navigationApiService.middleware)
    .concat(forumApiService.middleware)
    .concat(reportApiService.middleware)
    .concat(logoutCleanupHandler)
    .concat(apiErrorHandler)
})

setupListeners(store.dispatch)
